/**
 * Template domain logic (doc 02 §3.4; doc 05 §3 lifecycle). Pure + isomorphic so the
 * New-template modal (client) and the create route (server, authoritative) share one
 * rule set. The 8 client rules below are the prototype's; rule 9 (sequential numeric
 * placeholders) is the REAL-build addition Meta enforces server-side.
 */
/** Auto-sanitise to Meta's name format: trim → lowercase → spaces→_ → strip [^a-z0-9_]. */
export function sanitizeTemplateName(input) {
    return input
        .trim()
        .toLowerCase()
        .replace(/\s+/g, "_")
        .replace(/[^a-z0-9_]/g, "");
}
/** Placeholders in body order, e.g. "Hi {{1}}, order {{2}}" → ["1","2"]. */
export function detectPlaceholders(body) {
    return Array.from(body.matchAll(/\{\{\s*(\w+)\s*\}\}/g), (m) => m[1] ?? "");
}
/** The 8 prototype rules + sequential-numeric enforcement. Returns human-readable errors. */
export function validateTemplate(input) {
    const errors = [];
    const name = sanitizeTemplateName(input.name);
    const body = input.body;
    const trimmed = body.trim();
    if (!name)
        errors.push("Add a template name.");
    if (!trimmed)
        errors.push("Body can't be empty.");
    if (trimmed.length > 1024)
        errors.push("Body exceeds 1,024 characters.");
    if (/^\s*\{\{/.test(body))
        errors.push("Body can't start with a variable — add words first.");
    if (/\}\}\s*$/.test(body))
        errors.push("Body can't end with a variable — add words after.");
    if (/\}\}\s*\{\{/.test(body))
        errors.push("Two variables can't be adjacent — add text between them.");
    if (/\n/.test(body))
        errors.push("Remove line breaks.");
    if (/ {4,}/.test(body))
        errors.push("Remove runs of 4+ spaces.");
    // Rule 9 (REAL): placeholders must be sequential {{1}}…{{n}} (Meta rejects otherwise).
    const ph = detectPlaceholders(body);
    if (ph.length > 0) {
        const expected = ph.map((_, i) => String(i + 1));
        if (ph.join(",") !== expected.join(",")) {
            errors.push("Variables must be numbered sequentially: {{1}}, {{2}}, …");
        }
    }
    return errors;
}
/** Sample values keyed by variable label, for card previews (doc 02 §3.3 TPL_SAMPLE). */
export const TPL_SAMPLE = {
    "Customer name": "Ahmad",
    "Order ID": "1781",
    "Order total": "189.00",
    Courier: "DHL",
    "Tracking no.": "1234-5678",
    "Tracking URL": "track.my/p1781",
    "Expiry date": "15 Jun 2026",
    "Product name": "Ganzo G7 folder",
    "Product URL": "thepisau.com/g7",
    Discount: "20%",
    Product: "Victorinox chef knife",
    "Shop URL": "thepisau.com",
};
/**
 * Merge values into {{n}} for a preview. For each placeholder index n, use
 * explicit `values[n]`, else the sample for the mapped variable label, else the
 * label itself, else the raw {{n}} (doc 02 §3.3).
 */
export function fillTemplate(body, variables = [], values = {}) {
    const byIndex = new Map(variables.map((v) => [v.index, v.label]));
    return body.replace(/\{\{\s*(\w+)\s*\}\}/g, (raw, key) => {
        const idx = Number(key);
        if (values[key] !== undefined)
            return values[key];
        const label = byIndex.get(idx);
        if (label)
            return TPL_SAMPLE[label] ?? label;
        return raw;
    });
}
//# sourceMappingURL=template.js.map